import { createAction, type Reducer } from '@reduxjs/toolkit';
import { createTransform } from 'redux-persist';
import type { ShedTier } from '../storage/DeviceStorageBudget';
import { trimMetadataForStorage, type RecipeMetadataState } from './recipeMetadataSlice';
import { trimCookLogForStorage, type CookLogState } from './cookLogSlice';

/**
 * What a low-storage shed may drop from DISK, per tier — the redux half of the
 * quota ladder in `src/storage/DeviceStorageBudget.ts`.
 *
 * A shed evicts a slice from `persist:root`; it never touches the store. The
 * cook keeps working on the state they had, every cloud sync keeps publishing
 * the real state (a reset would have published defaults over the cloud copy
 * on the next debounced write — and an empty allergen list means "no
 * exclusions" to the planners), and the disk simply stops carrying that slice
 * for the rest of the session. On the next boot it comes back from Firestore.
 *
 * The rule is therefore "never evict the only copy from disk". Every slice
 * named here is re-seeded from Firestore on the next boot of a signed-in
 * account. A guest has no Firestore, so for a guest only the slices that are
 * refetched from an API or are purely cosmetic go.
 *
 * `recipeMetadata` is trimmed, not evicted: the disk copy keeps every entry
 * the outbox still owes the cloud and every entry carrying local-only cooking
 * history, and drops the rest (see `trimMetadataForStorage`).
 *
 * `cookLog` is trimmed the same way (#795): it holds every cook note now, and
 * an entry its outbox still owes the cloud is the only copy there is. The rest
 * comes back from Firestore on the next boot (`trimCookLogForStorage`).
 */
export const SHED_SLICES: Record<ShedTier, readonly string[]> = {
  mirrors: ['deals', 'announcements', 'feedback', 'navigation', 'pantrySync'],
  lists: ['groceryList', 'userPreferences', 'pantryLearning'],
};

/** The subset a guest can lose on the next boot: refetched from an API or cosmetic. */
const GUEST_SHEDDABLE: readonly string[] = ['deals', 'announcements', 'navigation', 'pantrySync'];

/**
 * Never, at any rung. `auth` is the session; `inventory` and `cookingSession`
 * have no cloud copy for a guest and are what the cook is doing right now;
 * `language` and `onboarding` are a few bytes that decide what the app looks
 * like on the next boot; `recipeMetadata` and `cookLog` hold outboxes (trimmed
 * on the mirrors rung, never evicted); `mealPlan` and
 * `recipeIndex` are not in this box at all (nested IndexedDB persists).
 */
export const NEVER_SHED: readonly string[] = [
  'auth',
  'inventory',
  'cookingSession',
  'language',
  'onboarding',
  'recipeMetadata',
  'cookLog',
  'mealPlan',
  'recipeIndex',
];

export const SHED_FOR_STORAGE = 'storage/shed' as const;

export const shedForStorage = createAction<ShedTier, typeof SHED_FOR_STORAGE>(SHED_FOR_STORAGE);

// What the disk no longer carries this session. Module state on purpose: the
// transform below runs inside redux-persist with no access to the store, and
// an eviction has to hold for every later write of that slice or the disk
// would simply grow back on the next dismissed announcement.
const evicted = new Set<string>();
let trimMetadata = false;
let trimCookLog = false;

/** Test seam. */
export const resetStorageShedding = (): void => {
  evicted.clear();
  trimMetadata = false;
  trimCookLog = false;
};

/** What the disk is currently missing, for reports and tests. */
export const evictedSlices = (): readonly string[] => [...evicted];

interface AuthLike {
  user?: unknown;
  isAuthenticated?: boolean;
  isGuest?: boolean;
}

const hasCloudCopy = (auth: AuthLike | undefined): boolean =>
  Boolean(auth?.user) && auth?.isAuthenticated === true && auth?.isGuest !== true;

// Same content, new identity. redux-persist stages a slice only when its
// reference changed, so this is what makes it re-serialise — through the
// transform, which is where the eviction happens.
const rekey = (value: unknown): unknown => {
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') return { ...(value as Record<string, unknown>) };
  return value;
};

/**
 * Wrap the root reducer so one action marks a whole tier as evicted and
 * nudges those slices through redux-persist again. State is unchanged in
 * every way that matters: same values, new object identities.
 */
export const withStorageShedding = <S extends Record<string, unknown>>(
  reducer: Reducer<S>,
): Reducer<S> => {
  return (state, action) => {
    if (!state || !shedForStorage.match(action)) return reducer(state, action);

    const tier = action.payload;
    const signedIn = hasCloudCopy(state.auth as AuthLike | undefined);
    const keys = signedIn
      ? SHED_SLICES[tier]
      : SHED_SLICES[tier].filter(key => GUEST_SHEDDABLE.includes(key));
    const trim = signedIn && tier === 'mirrors';
    if (keys.length === 0 && !trim) return state;

    const nudged: Record<string, unknown> = { ...state };
    for (const key of keys) {
      evicted.add(key);
      if (key in state) nudged[key] = rekey(state[key]);
    }
    if (trim) {
      trimMetadata = true;
      nudged.recipeMetadata = rekey(state.recipeMetadata);
      if ('cookLog' in state) {
        trimCookLog = true;
        nudged.cookLog = rekey(state.cookLog);
      }
    }
    return reducer(nudged as S, action);
  };
};

/**
 * The disk side of a shed. Registered on the root persist; an evicted slice
 * is written as nothing (redux-persist drops the key, and the slice
 * re-initialises on the next boot), and `recipeMetadata` is written trimmed.
 * Rehydration is untouched.
 */
export const storageBudgetTransform = createTransform(
  (inbound: unknown, key: string | number | symbol) => {
    if (typeof key !== 'string') return inbound;
    if (evicted.has(key)) return undefined;
    if (key === 'recipeMetadata' && trimMetadata) {
      return trimMetadataForStorage(inbound as RecipeMetadataState);
    }
    if (key === 'cookLog' && trimCookLog) {
      return trimCookLogForStorage(inbound as CookLogState);
    }
    return inbound;
  },
  (outbound: unknown) => outbound,
);
